/**
 * AccountSettings.tsx — Admin self-service settings page (Phase 9 SC-5).
 *
 * Two cards:
 *   1. Display Name — Input maxLength=80 + char counter + Save Name
 *   2. Change Password — current/new/confirm + Change Password
 *
 * Security:
 *   - Change password → success toast + redirect to /login after 1.5s
 *     (communicates D-09-12 token_version bump to the user)
 *   - Self-service only — uses req.user.id on the server side (T-09-22)
 *
 * Conforms to 09-UI-SPEC Screen 2.
 */

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi, operatorsApi, ApiRequestError } from '../lib/api-client.js';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
  useToast,
} from '@crm/ui';

export function AccountSettings() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const qc = useQueryClient();

  // Pre-populate display name from /auth/me (cached by AdminGuard)
  const meQ = useQuery({ queryKey: ['me'], queryFn: () => authApi.me() });

  // ---------------------------------------------------------------------------
  // Display Name card
  // ---------------------------------------------------------------------------

  const [displayName, setDisplayName] = React.useState('');
  const [nameError, setNameError] = React.useState<string | null>(null);

  // Sync initial value from /auth/me
  React.useEffect(() => {
    if (meQ.data?.displayName) {
      setDisplayName(meQ.data.displayName);
    }
  }, [meQ.data?.displayName]);

  const editNameMut = useMutation({
    mutationFn: () => operatorsApi.editDisplayName({ displayName: displayName.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] });
      addToast('Display name updated', 'success');
    },
    onError: (err) => {
      const msg = err instanceof ApiRequestError ? err.message : 'Update failed';
      addToast(msg, 'danger');
    },
  });

  const handleSaveName = (e: React.FormEvent) => {
    e.preventDefault();
    setNameError(null);
    const trimmed = displayName.trim();
    if (!trimmed) {
      setNameError('Display name is required.');
      return;
    }
    if (trimmed.length > 80) {
      setNameError('Display name must be 80 characters or fewer.');
      return;
    }
    editNameMut.mutate();
  };

  // ---------------------------------------------------------------------------
  // Change Password card
  // ---------------------------------------------------------------------------

  const [currentPassword, setCurrentPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [pwError, setPwError] = React.useState<string | null>(null);

  const changePasswordMut = useMutation({
    mutationFn: () =>
      operatorsApi.changePassword({ currentPassword, newPassword }),
    onSuccess: () => {
      addToast('Password updated. You will be signed out.', 'success');
      // Redirect to /login after ~1.5s — communicates the token_version bump
      setTimeout(() => {
        void authApi.logout().catch(() => {});
        qc.clear();
        navigate('/login', { replace: true });
      }, 1500);
    },
    onError: (err) => {
      const msg =
        err instanceof ApiRequestError
          ? err.message
          : 'Password change failed';
      // Map common server error to UI-SPEC copy
      const display =
        msg.toLowerCase().includes('incorrect') ||
        msg.toLowerCase().includes('invalid') ||
        msg.toLowerCase().includes('wrong')
          ? 'Current password is incorrect.'
          : msg;
      setPwError(display);
    },
  });

  const handleChangePassword = (e: React.FormEvent) => {
    e.preventDefault();
    setPwError(null);
    if (newPassword !== confirmPassword) {
      setPwError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setPwError('New password must be at least 8 characters.');
      return;
    }
    changePasswordMut.mutate();
  };

  return (
    <div className="max-w-lg mx-auto flex flex-col gap-6">
      <h1 className="text-xl font-semibold text-text">Account Settings</h1>

      {/* Card 1 — Display Name */}
      <Card>
        <CardHeader>
          <CardTitle>Display Name</CardTitle>
        </CardHeader>
        <CardContent>
          <form id="display-name-form" onSubmit={handleSaveName} className="flex flex-col gap-3">
            <p className="text-sm text-text-muted">
              Shown to other operators in the Assignments view and Audit Log.
            </p>
            <Input
              label="Display name"
              type="text"
              maxLength={80}
              autoComplete="name"
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
                setNameError(null);
              }}
              disabled={editNameMut.isPending}
              error={nameError ?? undefined}
            />
            <p className="text-xs text-text-subtle">
              {80 - displayName.length} characters remaining
            </p>
          </form>
        </CardContent>
        <CardFooter>
          <Button
            form="display-name-form"
            type="submit"
            size="sm"
            loading={editNameMut.isPending}
            disabled={!displayName.trim()}
          >
            Save Name
          </Button>
        </CardFooter>
      </Card>

      {/* Card 2 — Change Password */}
      <Card>
        <CardHeader>
          <CardTitle>Change Password</CardTitle>
        </CardHeader>
        <CardContent>
          <form id="change-pw-form" onSubmit={handleChangePassword} className="flex flex-col gap-3">
            <Input
              label="Current password"
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value);
                setPwError(null);
              }}
              disabled={changePasswordMut.isPending}
            />
            <Input
              label="New password"
              type="password"
              autoComplete="new-password"
              required
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setPwError(null);
              }}
              disabled={changePasswordMut.isPending}
            />
            <Input
              label="Confirm new password"
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setPwError(null);
              }}
              disabled={changePasswordMut.isPending}
            />
            {pwError && (
              <p className="text-sm text-danger" role="alert">
                {pwError}
              </p>
            )}
          </form>
        </CardContent>
        <CardFooter>
          <Button
            form="change-pw-form"
            type="submit"
            size="sm"
            loading={changePasswordMut.isPending}
          >
            Change Password
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
